// src/components/MyBookings/MyBookings.js
import React, { useCallback, useEffect, useState } from "react";
import {
  Button,
  Card,
  Skeleton,
  Space,
  Tabs,
  Tag,
  Typography,
  Divider,
  message,
  FloatButton
} from "antd";
import {
  CalendarOutlined,
} from "@ant-design/icons";
import { useNavigate, useLocation } from "react-router-dom";

import { get as httpGet } from "../../utils/request";
import "./my-bookings.scss";
import Navbar from "../Navbar/Navbar";
import Footer from "../Footer/Footer";
import { CalendarDays } from "lucide-react";
import {FlightsSummary} from "./FlightsSummary";
import {
  bookingSummary,
  cleanDisplayAmount,
  customerOrderNumber,
  formatBookingDate,
  mergeOrders,
  serviceMeta,
  statusTag,
} from "./bookingDisplay";

const { Title, Text } = Typography;

const ENTER_THRESHOLD = 300;
const LEAVE_THRESHOLD = 300;
const BOOKING_TABS = new Set(["all", "esim", "flights", "thingsToDo", "airportLounge"]);

const CustomerReferences = ({ booking }) => {
  return (
    <div className="booking-references">
      <span>Order No: <strong>{customerOrderNumber(booking)}</strong></span>
    </div>
  );
};

const ServiceBookingCard = ({ booking, onOpen }) => {
  const meta = serviceMeta(booking.serviceType);
  const status = statusTag(booking);
  const summary = bookingSummary(booking);
  const canOpen = booking.serviceType === "esim";

  return (
    <Card
      className={`booking-card ${canOpen ? "booking-card-clickable" : ""}`}
      bodyStyle={{ padding: 0 }}
      onClick={canOpen ? () => onOpen(booking) : undefined}
      hoverable={canOpen}
    >
      <div className="booking-card-header">
        <div className="booking-card-service">
          <span className="booking-card-icon" aria-hidden="true">{meta.icon}</span>
          <div>
            <Title level={4}>{meta.title}</Title>
            <span className="booking-card-date">Booked {formatBookingDate(booking.orderDate) || "date unavailable"}</span>
          </div>
        </div>
        <div className="booking-card-reference-block">
          <CustomerReferences booking={booking} />
          <Tag color={status.color}>{status.text}</Tag>
        </div>
      </div>

      <div className="booking-card-body">
        <div className="booking-card-summary">
          <h3>{summary.title}</h3>
          {summary.fields.length > 0 && (
            <div className="booking-card-fields">
              {summary.fields.map((item) => (
                <div key={`${item.label}:${item.value}`}>
                  <span>{item.label}</span>
                  <strong>{item.value}</strong>
                </div>
              ))}
            </div>
          )}
        </div>
        <div className="booking-card-total">
          <span>Paid Price</span>
          <strong>{cleanDisplayAmount(booking)}</strong>
          {canOpen && <Button onClick={(event) => { event.stopPropagation(); onOpen(booking); }}>View details</Button>}
        </div>
      </div>
    </Card>
  );
};

/* -------------------------------- Component ------------------------------- */
const MyBookings = ({ embedded = false }) => {
  const navigate = useNavigate();
  const location = useLocation();

  const [tab, setTab] = useState(() => BOOKING_TABS.has(location.state?.tab) ? location.state.tab : "all");
  const [loading, setLoading] = useState(true);

  const [bookings, setBookings] = useState([]);
  const [totalCount, setTotalCount] = useState(null);
  const [hasNext, setHasNext] = useState(false);
  const [cursor, setCursor] = useState(null);

  const [useInlineButton, setUseInlineButton] = useState(false);

  const fetchOrders = useCallback(async () => {
    try {
      const jwtToken = localStorage.getItem("teleport_token");
      if (!jwtToken) {
        setLoading(false);
        if (!embedded) {
          navigate("/login", { replace: true });
        }
        return;
      }

      setLoading(true);

      const params = {
        serviceType: tab !== "all" ? tab : undefined,
      };

      const result = await httpGet("/order/getAllOrders", params);
      const orders = Array.isArray(result?.orders) ? result.orders : [];
      setBookings(orders);
      if (Number.isFinite(Number(result?.totalCount))) {
        setTotalCount(Number(result.totalCount));
      } else if (tab === "all") {
        // Older API deployments did not return a lifetime count. Walk every
        // cursor page instead of presenting the first five records as the total.
        let lifetimeOrders = orders;
        let nextCursor = result?.cursor;
        let hasAnotherPage = Boolean(result?.hasNext);
        while (hasAnotherPage && nextCursor) {
          const nextResult = await httpGet("/order/getAllOrders", {
            updatedAt: nextCursor.updatedAt,
            id: nextCursor.id,
          });
          lifetimeOrders = mergeOrders(lifetimeOrders, Array.isArray(nextResult?.orders) ? nextResult.orders : []);
          hasAnotherPage = Boolean(nextResult?.hasNext);
          nextCursor = nextResult?.cursor;
        }
        setTotalCount(lifetimeOrders.length);
      } else {
        setTotalCount(null);
      }
      setHasNext(Boolean(result?.hasNext));
      setCursor(result?.cursor || null);
    } catch (err) {
      console.error(err);
      message.error("Unable to load bookings.");
    } finally {
      setLoading(false);
    }
  }, [embedded, navigate, tab]);

  const fetchMoreOrders = async () => {
    try {
      setLoading(true);

      const params = {
        serviceType: tab !== "all" ? tab : undefined,
        updatedAt: !!cursor ? cursor.updatedAt : undefined,
        id: !!cursor ? cursor.id : undefined,
      };

      const result = await httpGet("/order/getAllOrders", params);
      const orders = Array.isArray(result?.orders) ? result.orders : [];
      setBookings((prev) => mergeOrders(prev, orders));
      if (Number.isFinite(Number(result?.totalCount))) setTotalCount(Number(result.totalCount));
      setHasNext(Boolean(result?.hasNext));
      setCursor(result?.cursor || null);
    } catch (err) {
      console.error(err);
      message.error("Unable to load more bookings.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchOrders();
  }, [fetchOrders]);

  useEffect(() => {
    const handleScroll = () => {
      const { scrollTop, scrollHeight, clientHeight } =
        document.documentElement;
      const distanceToBottom = scrollHeight - (scrollTop + clientHeight);

      if (!useInlineButton && distanceToBottom <= ENTER_THRESHOLD) {
        setUseInlineButton(true);
      } else if (useInlineButton && distanceToBottom > LEAVE_THRESHOLD) {
        setUseInlineButton(false);
      }
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, [useInlineButton]);

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleBookingOpen = (booking) => {
    if (booking.serviceType === "esim") navigate(`/my-bookings/esim/${booking.orderId}`);
  };

  if (loading) {
    const loadingContent = (
      <div className="my-bookings my-bookings-loading">
        <div style={{ padding: 16 }}>
          <Skeleton active paragraph={{ rows: 1 }} />
          <div style={{ marginTop: 16 }}>
            <Skeleton active avatar paragraph={{ rows: 3 }} />
          </div>
          <div style={{ marginTop: 16 }}>
            <Skeleton active avatar paragraph={{ rows: 3 }} />
          </div>
        </div>
      </div>
    );

    if (embedded) return loadingContent;

    return (
      <>
        <Navbar />
        <div className="layout-content page">
          <section className="container p-0">{loadingContent}</section>
        </div>
        <Footer />
      </>
    );
  }

  const content = (
    <div className="my-bookings">
      <div className="my-bookings-hero">
        <div>
          <Title level={2} className="my-bookings-title">
            My Bookings
          </Title>
          <Text className="my-bookings-subtitle">
            Manage your travel reservations, eSIMs, and upcoming trips in one place.
          </Text>
        </div>
        <div className="my-bookings-hero-card">
          <CalendarDays size={24} />
          <span>{totalCount === null ? "—" : totalCount.toLocaleString()}</span>
          <small>lifetime orders</small>
        </div>
      </div>

      <div className="my-bookings-tabs">
        <Tabs
          activeKey={tab}
          onChange={setTab}
          items={[
            {
              key: "all",
              label: (
                <Space>
                  <span>⭐</span>All
                </Space>
              ),
            },
            {
              key: "esim",
              label: (
                <Space>
                  <span>📱</span>Travel eSIM
                </Space>
              ),
            },
            {
              key: "flights",
              label: (
                <Space>
                  <span>✈️</span>Flights
                </Space>
              ),
            },
            {
              key: "thingsToDo",
              label: (
                <Space>
                  <span>🎟️</span>Things To Do
                </Space>
              ),
            },
            {
              key: "airportLounge",
              label: (
                <Space>
                  <span>🛋️</span>Airport Lounge
                </Space>
              ),
            },
          ]}
        />
      </div>

      <div className="my-booking-list">
        {bookings.length === 0 ? (
          <Card style={{ textAlign: "center", padding: 24 }}>
            <div
              style={{
                display: "flex",
                justifyContent: "center",
                marginBottom: 16,
              }}
            >
              <div
                style={{
                  width: 96,
                  height: 96,
                  borderRadius: "9999px",
                  background: "#FFF7E6",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  fontSize: 40,
                }}
              >
                <CalendarOutlined style={{ color: "#d48806" }} />
              </div>
            </div>
            <Title level={3} style={{ marginBottom: 8 }}>
              No bookings yet
            </Title>
            <Text type="secondary">
              Start planning your next adventure and your bookings will appear
              here
            </Text>
          </Card>
        ) : (
          <Space direction="vertical" size={16} style={{ width: "100%" }}>
            {bookings.map((booking, idx) => {
              if (booking.serviceType === "flights") {
                return <FlightsSummary key={`${booking.orderId}-${idx}`} booking={booking} />;
              }
              return <ServiceBookingCard key={`${booking.orderId}-${idx}`} booking={booking} onOpen={handleBookingOpen} />;
            })}
          </Space>
        )}

        {hasNext ? (
          <div className="load-more-btn-wrapper">
            <Button className="load-more-btn" onClick={fetchMoreOrders}>
              Load more
            </Button>
          </div>
        ) : (
          <Divider>No more bookings</Divider>
        )}
      </div>

      {!useInlineButton && (
        <FloatButton.BackTop
          shape="square"
          type="primary"
          rootClassName="dynamic-back-top"
          style={{ backgroundColor: "#CBA135", borderColor: "#CBA135" }}
        />
      )}
      {useInlineButton && (
        <div className="inline-back-top">
          <FloatButton.BackTop
            type="primary"
            shape="square"
            onClick={scrollToTop}
            style={{
              position: "static",
              backgroundColor: "#CBA135",
              borderColor: "#CBA135",
            }}
          />
        </div>
      )}
    </div>
  );

  if (embedded) return content;

  return (
    <>
      <Navbar />
      <div className="layout-content page">
        <section className="container p-0">{content}</section>
      </div>
      <Footer />
    </>
  );
};

export default MyBookings;
